import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { usePopup } from "../../../contexts/PopupContext";
import ShareModal from "../ShareModal";
import SharedWithMeSection from "../SharedWithMeSection";
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  Share2,
  LayoutDashboard,
} from "lucide-react";

import { getCases, activateCase, deleteCase } from "../../../../api/cases";
import { getAvailableWikiData } from "../../../../api/wiki";
import { usePortalTarget } from "./sidebarUtils";
import { useLayout } from "../../../contexts/LayoutContext";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const WIKI_SUB_SECTIONS = [
  "Overview",
  "Key Employees",
  "Court Cases",
  "Neighbors",
  "Domains",
  "Sanctions",
  "Gov Awards",
  "BOLs",
  "Crypto",
  "Reports",
];

const WikiSidebarPortal = ({ onAddCompany, triggerReload }) => {
  const navigate = useNavigate();
  const [shareTarget, setShareTarget] = useState(null);
  const portalTarget = usePortalTarget("wiki-portal-slot");
  const { currentAppPage, leftOpen } = useLayout();
  const [cases, setCases] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [currentSubSection, setCurrentSubSection] = useState("Overview");
  const [expandedCases, setExpandedCases] = useState({});
  const [caseAvailableData, setCaseAvailableData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [inactiveExpanded, setInactiveExpanded] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [visibleInactiveCount, setVisibleInactiveCount] = useState(10);
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();

  const activeCases = useMemo(
    () => cases.filter((c) => c.active !== false),
    [cases],
  );
  const inactiveCases = useMemo(
    () => cases.filter((c) => c.active === false),
    [cases],
  );

  // Emit active count so sidebar label can show "Wiki 3/10"
  // Only show the count when the wiki section is expanded (portalTarget exists)
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("sidebar:wiki-counts", {
        detail: {
          active: activeCases.length,
          maxActive: 10,
          showCount: !!portalTarget,
        },
      }),
    );
  }, [activeCases.length, portalTarget]);

  // Hide count when wiki section collapses (portal unmounts)
  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent("sidebar:wiki-counts", {
          detail: { active: 0, maxActive: 10, showCount: false },
        }),
      );
    };
  }, []);

  const loadCases = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getCases();
      const data = response?.data || [];
      setCases(data);
    } catch (e) {
      console.error("Error loading cases:", e);
      setCases([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases, triggerReload]);

  /* ── Search registry ── */
  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    const items = [];

    (activeCases || []).forEach((c) => {
      const available = caseAvailableData[c.case];
      // Only show sub-sections we know have data
      const children = available
        ? WIKI_SUB_SECTIONS.filter((s) => available.has(s)).map((sub) => ({
            id: `wiki-${c.case}-${sub}`,
            label: sub,
            keywords: [c.case, sub.toLowerCase()],
            event: {
              navigateTo: "/wiki",
              name: "sidebar:wiki-company-selected",
              detail: { company: c.case },
              then: {
                name: "sidebar:wiki-subsection-selected",
                detail: { subSection: sub },
              },
            },
          }))
        : [];

      items.push({
        id: `wiki-${c.case}`,
        label: c.case,
        keywords: ["company", "case", "wiki"],
        statusDot: "#4ade80", // green — active
        event: {
          navigateTo: "/wiki",
          name: "sidebar:wiki-company-selected",
          detail: { company: c.case },
        },
        // Only attach children if we have loaded data for this case
        ...(children.length > 0 ? { children } : {}),
      });
    });

    (inactiveCases || []).forEach((c) => {
      items.push({
        id: `wiki-inactive-${c.case}`,
        label: c.case,
        keywords: ["company", "case", "inactive"],
        statusDot: "#f87171", // red — inactive
        event: {
          navigateTo: "/wiki",
          name: "sidebar:wiki-company-selected",
          detail: { company: c.case },
        },
      });
    });

    register("/wiki", items);
    return () => unregister("/wiki");
  }, [activeCases, inactiveCases, caseAvailableData, register, unregister]);

  const loadAvailableData = useCallback(
    async (caseName) => {
      if (caseAvailableData[caseName]) return;
      try {
        const response = await getAvailableWikiData(caseName);
        if (response && Array.isArray(response)) {
          const available = new Set(["Overview"]);
          response.forEach((item) => {
            if (item.data) available.add(item.sub_section);
          });
          setCaseAvailableData((prev) => ({ ...prev, [caseName]: available }));
        }
      } catch (e) {
        console.error("Error loading wiki data for", caseName, e);
      }
    },
    [caseAvailableData],
  );

  useEffect(() => {
    if (currentAppPage !== "/wiki") {
      setCurrentCompany(null);
      setCurrentSubSection("Overview");
    }
  }, [currentAppPage]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.company !== undefined) setCurrentCompany(e.detail.company);
    };
    window.addEventListener("sidebar:select-company", handler);
    return () => window.removeEventListener("sidebar:select-company", handler);
  }, []);

  useEffect(() => {
    const handler = () => loadCases();
    window.addEventListener("sidebar:refresh-cases", handler);
    return () => window.removeEventListener("sidebar:refresh-cases", handler);
  }, [loadCases]);

  useEffect(() => {
    if (currentCompany) {
      setExpandedCases((prev) => ({ ...prev, [currentCompany]: true }));
      loadAvailableData(currentCompany);
    }
  }, [currentCompany, loadAvailableData]);

  /* ── handlers ── */

  /* ── Breadcrumb: emit active sub-item ── */
  const emitWikiBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/wiki") return;
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/wiki",
          subLabel: currentCompany
            ? currentSubSection !== "Overview"
              ? `${currentCompany} › ${currentSubSection}`
              : currentCompany
            : null,
          subIcon: null,
          hasAdd: true,
          addKind: "wiki",
          addLabel: "Add company",
        },
      }),
    );
  }, [currentCompany, currentSubSection, currentAppPage]);

  useEffect(() => {
    emitWikiBreadcrumb();
  }, [emitWikiBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/wiki") emitWikiBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitWikiBreadcrumb]);

  /* ── Breadcrumb: listen for add action ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/wiki") onAddCompany();
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [onAddCompany]);

  const toggleCaseExpand = (name, e) => {
    e.stopPropagation();
    const willExpand = !expandedCases[name];
    setExpandedCases((prev) => ({ ...prev, [name]: willExpand }));
    if (willExpand) loadAvailableData(name);
  };

  // Clear active state when another section becomes active
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "wiki") {
        setCurrentCompany(null);
        setCurrentSubSection("Overview");
      }
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  const handleSelect = (name) => {
    setCurrentCompany(name);
    setCurrentSubSection("Overview");
    setConfirmAction(null);
    window.dispatchEvent(
      new CustomEvent("sidebar:wiki-company-selected", {
        detail: { company: name },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("sidebar:wiki-subsection-selected", {
        detail: { subSection: "Overview" },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "wiki" },
      }),
    );
    if (currentAppPage !== "/wiki") navigate("/wiki");
  };

  const handleSubSelect = (caseName, subSection) => {
    if (currentCompany !== caseName) {
      setCurrentCompany(caseName);
      window.dispatchEvent(
        new CustomEvent("sidebar:wiki-company-selected", {
          detail: { company: caseName },
        }),
      );
    }
    setCurrentSubSection(subSection);
    window.dispatchEvent(
      new CustomEvent("sidebar:wiki-subsection-selected", {
        detail: { subSection },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "wiki" },
      }),
    );
    if (currentAppPage !== "/wiki") navigate("/wiki");
  };

  const handleActivate = async (caseName) => {
    try {
      await activateCase(caseName, true);
      await loadCases();
    } catch (e) {
      console.error("Error activating case:", e);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleDeactivate = async (caseName) => {
    try {
      await activateCase(caseName, false);
      if (currentCompany === caseName) {
        setCurrentCompany(null);
        setCurrentSubSection("Overview");
      }
      await loadCases();
    } catch (e) {
      console.error("Error deactivating case:", e);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleDelete = async (caseName) => {
    try {
      await deleteCase(caseName);
      if (currentCompany === caseName) {
        setCurrentCompany(null);
        setCurrentSubSection("Overview");
      }
      await loadCases();
    } catch (e) {
      console.error("Error deleting case:", e);
    } finally {
      setConfirmAction(null);
    }
  };

  const activeAtMax = activeCases.length >= 10;

  /* ── Context menu registrations ── */
  useContextMenu("wiki-active-case", [
    {
      label: "Share",
      icon: Share2,
      action: (payload) => {
        setPopUpComponent(
          <ShareModal
            onClose={() => setIsPopUpOpen(false)}
            contentType="wiki_case"
            documentId=""
            companyName={payload.case}
            itemName={payload.case}
          />,
        );
        setIsPopUpOpen(true);
      },
    },
    {
      label: "Deactivate",
      icon: ChevronDown,
      hidden: () => inactiveCases.length >= 90,
      action: (payload) => {
        setConfirmAction({ caseName: payload.case, action: "deactivate" });
      },
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        setConfirmAction({ caseName: payload.case, action: "delete" });
      },
    },
  ]);

  useContextMenu("wiki-inactive-case", [
    {
      label: "Share",
      icon: Share2,
      action: (payload) => {
        setPopUpComponent(
          <ShareModal
            onClose={() => setIsPopUpOpen(false)}
            contentType="wiki_case"
            documentId=""
            companyName={payload.case}
            itemName={payload.case}
          />,
        );
        setIsPopUpOpen(true);
      },
    },
    {
      label: "Activate",
      icon: ChevronUp,
      hidden: () => activeAtMax,
      action: (payload) => {
        setConfirmAction({ caseName: payload.case, action: "activate" });
      },
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        setConfirmAction({ caseName: payload.case, action: "delete" });
      },
    },
  ]);

  /* ── reusable case row ── */

  const renderCaseRow = (caseItem, isActiveCase) => {
    const name = caseItem.case;
    const isSelected = name === currentCompany;
    const isExpanded = !!expandedCases[name];
    const available = caseAvailableData[name];
    const visibleSubs = available
      ? WIKI_SUB_SECTIONS.filter((s) => available.has(s))
      : null;
    const hasConfirm = confirmAction?.caseName === name;

    return (
      <div
        key={name}
        className="group/case"
        data-context-menu={
          isActiveCase ? "wiki-active-case" : "wiki-inactive-case"
        }
        data-context-payload={JSON.stringify(caseItem)}
      >
        {/* ── Case name row ── */}
        <div className="flex items-center gap-x-1">
          <span
            className={[
              "w-1.5 h-1.5 rounded-full shrink-0 ml-0.5",
              isActiveCase ? "bg-emerald-400" : "bg-red-400",
            ].join(" ")}
          />
          <button
            onClick={() => handleSelect(name)}
            title={name}
            className={[
              "flex-1 min-w-0 text-left rounded-md px-1.5 py-1.5 text-[12px] transition-all duration-200 truncate",
              isSelected
                ? "text-[#0EA5E9] font-medium"
                : "text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200",
            ].join(" ")}
          >
            {name}
          </button>
          <button
            onClick={(e) => toggleCaseExpand(name, e)}
            className="p-0.5 rounded-md hover:bg-slate-200/60 dark:hover:bg-slate-700/40 transition-all shrink-0"
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? (
              <ChevronUp
                className="h-3 w-3 text-slate-400 dark:text-gray-500"
                strokeWidth={2}
              />
            ) : (
              <ChevronDown
                className="h-3 w-3 text-slate-400 dark:text-gray-500 opacity-0 group-hover/case:opacity-100 transition-opacity"
                strokeWidth={2}
              />
            )}
          </button>
        </div>

        {/* ── Inline confirmation (shown when right-click action triggers it) ── */}
        <div
          className={[
            "overflow-hidden transition-all duration-200 ease-in-out ml-4",
            hasConfirm
              ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
              : "max-h-0 opacity-0",
          ].join(" ")}
        >
          {hasConfirm && (
            <div className="flex items-center gap-x-2 py-1 px-1">
              <span className="text-[11px] text-slate-500 dark:text-gray-400">
                {confirmAction.action === "delete"
                  ? "Delete?"
                  : confirmAction.action === "activate"
                    ? "Activate?"
                    : "Deactivate?"}
              </span>
              <button
                onClick={() => setConfirmAction(null)}
                className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmAction.action === "delete") handleDelete(name);
                  else if (confirmAction.action === "activate")
                    handleActivate(name);
                  else handleDeactivate(name);
                }}
                className={[
                  "text-[11px] px-2 py-0.5 rounded border transition-colors",
                  confirmAction.action === "delete"
                    ? "border-red-300 dark:border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                    : confirmAction.action === "activate"
                      ? "border-emerald-300 dark:border-emerald-500/30 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
                      : "border-amber-300 dark:border-amber-500/30 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10",
                ].join(" ")}
              >
                Confirm
              </button>
            </div>
          )}
        </div>

        {/* ── Sub-sections ── */}
        <div
          className={[
            "overflow-hidden transition-all duration-200 ease-in-out",
            isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0",
          ].join(" ")}
        >
          <div className="ml-4 mt-0.5 flex flex-col gap-y-px border-l border-slate-200/40 dark:border-slate-700/30 pl-2">
            {visibleSubs ? (
              visibleSubs.map((sub) => {
                const isSubActive = isSelected && currentSubSection === sub;
                return (
                  <button
                    key={sub}
                    onClick={() => handleSubSelect(name, sub)}
                    className={[
                      "w-full text-left rounded-md px-2 py-1 text-[11px] transition-all duration-200 truncate",
                      isSubActive
                        ? "text-[#0EA5E9] font-medium"
                        : "text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300",
                    ].join(" ")}
                  >
                    {sub}
                  </button>
                );
              })
            ) : (
              <div className="px-2 py-1 text-[11px] text-slate-400 dark:text-gray-500">
                Loading…
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ── portal content ── */

  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  const content = (
    <div className="flex flex-col gap-y-0.5">
      {!isInNavbar && (
        <button
          onClick={onAddCompany}
          className={[
            "group w-full flex items-center gap-x-2 rounded-md px-2 py-1 text-[12px] transition-all duration-200",
            "text-slate-400 dark:text-gray-500",
            "hover:text-[#0EA5E9] hover:bg-slate-100/60 dark:hover:bg-slate-800/30",
          ].join(" ")}
          title="Add company"
        >
          <Plus className="h-3 w-3 shrink-0" strokeWidth={2} />
          <span>Add company</span>
        </button>
      )}

      {isLoading && cases.length === 0 && (
        <div className="px-2 py-1.5 text-[11px] text-slate-400 dark:text-gray-500">
          Loading…
        </div>
      )}

      {/* Active cases */}
      {activeCases.map((c) => renderCaseRow(c, true))}

      {/* Inactive cases directory */}
      {inactiveCases.length > 0 && (
        <div className="mt-1">
          <button
            onClick={() => {
              setInactiveExpanded((v) => {
                if (v) setVisibleInactiveCount(10); // reset on collapse
                return !v;
              });
            }}
            className="w-full flex items-center justify-between rounded-md px-2 py-1 text-[11px] text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors"
          >
            <span>Inactive</span>
            <div className="flex items-center gap-x-1.5">
              <span className="text-[10px]">
                <span className="text-red-400">{inactiveCases.length}</span>
                <span className="text-slate-400 dark:text-gray-500">/90</span>
              </span>
              {inactiveExpanded ? (
                <ChevronUp className="h-3 w-3" strokeWidth={2} />
              ) : (
                <ChevronDown className="h-3 w-3" strokeWidth={2} />
              )}
            </div>
          </button>
          <div
            className={[
              "overflow-hidden transition-all duration-200 ease-in-out",
              inactiveExpanded
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0",
            ].join(" ")}
          >
            <div className="ml-1 border-l border-slate-200/40 dark:border-slate-700/30 pl-1">
              {inactiveCases
                .slice(0, visibleInactiveCount)
                .map((c) => renderCaseRow(c, false))}
              {visibleInactiveCount < inactiveCases.length && (
                <div className="flex justify-center mt-1">
                  <button
                    onClick={() => setVisibleInactiveCount((v) => v + 10)}
                    className="rounded-md px-3 py-1 text-[11px] text-slate-400 dark:text-gray-500 hover:text-[#0EA5E9] transition-colors"
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <SharedWithMeSection
        contentType="wiki"
        onSelect={(item) => {
          window.dispatchEvent(
            new CustomEvent("sidebar:wiki-company-selected", {
              detail: { company: item.target },
            }),
          );
          if (currentAppPage !== "/wiki") navigate("/wiki");
        }}
      />
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default WikiSidebarPortal;
